import { useApiClient } from '../../api/useApiClient';

/**
 * Servicio para operaciones relacionadas con documentos
 * Encapsula todas las llamadas API relacionadas con documentos
 */
class DocService {
    constructor(apiClient) {
        this.apiClient = apiClient;
    }

    /**
     * Buscar documentos por tipo y filtros
     * @param {string} tipoDoc - Tipo de documento
     * @param {Object} filters - Filtros de búsqueda
     * @param {Object} pagination - Configuración de paginación { page, size }
     * @returns {Promise} Response con documentos encontrados
     */
    async searchDocuments(tipoDoc, filters, pagination = { page: 0, size: 10 }, sort = []) {
        const { page, size } = pagination;
        const payload = {
            ...filters,
            sort
        };
        return this.apiClient.post(
            `/api/v1/series/${tipoDoc}/docs/search`,
            payload,
            { params: { page, size } }
        );
    }

    /**
     * Obtener especificación de metadata para un tipo de documento
     * @param {string} tipoDoc - Tipo de documento
     * @returns {Promise} Response con fieldSpec
     */
    async getMetadataSpec(tipoDoc) {
        return this.apiClient.get(`/api/v1/series/${tipoDoc}/metadata-specs`);
    }

    async getFieldSuggestions(tipoDoc, fieldName, query, size = 10) {
        return this.apiClient.get(`/api/v1/series/${tipoDoc}/suggest/${fieldName}`, {
            params: { q: query, size }
        });
    }

    /**
     * Descargar documento como blob
     * @param {string} idDoc - ID del documento
     * @returns {Promise} Response con blob del documento
     */
    async downloadDocument(idDoc) {
        return this.apiClient.get(
            `/api/v1/docs/${idDoc}/stream/download`,
            { responseType: 'blob' }
        );
    }

    /**
     * Stream de documento para visualización u otras operaciones
     * @param {string} idDoc - ID del documento
     * @param {string} operation - Tipo de operación (view, download, review, approval, signature).
     *   Cada una tiene su propio rol en el backend: `view`/`download` son de VIEWER, y `review`,
     *   `approval` y `signature` son de cada rol de workflow sobre los documentos que esperan su
     *   acción. Usar `view` desde el tab de Firma dejaba afuera al firmante mono-rol (#119).
     * @returns {Promise} Response con blob del documento
     */
    async streamDocument(idDoc, operation = 'view') {
        return this.apiClient.get(
            `/api/v1/docs/${idDoc}/stream/${operation}`,
            { responseType: 'arraybuffer' }
        );
    }

    /**
     * Obtener auditoría de un documento
     * @param {string} idDoc - ID del documento
     * @returns {Promise} Response con eventos de auditoría
     */
    async getAudit(idDoc) {
        return this.apiClient.get(`/api/v1/docs/${idDoc}/audit`);
    }

    /**
     * Construir URL completa de descarga (para copiar al portapapeles)
     * @param {string} idDoc - ID del documento
     * @returns {string} URL completa de descarga
     */
    getDownloadUrl(idDoc) {
        const baseURL = process.env.REACT_APP_API_URL || '';
        return `${baseURL}/api/v1/docs/${idDoc}/stream/download`;
    }
}

/**
 * Hook personalizado para usar el servicio de documentos
 * @returns {DocService} Instancia del servicio de documentos
 */
export const useDocService = () => {
    const apiClient = useApiClient();
    return new DocService(apiClient);
};

// Backward compatibility alias
export const useDocumentService = useDocService;

export default DocService;
